<template>
  <div
    v-if="visible"
    data-testid="qv-tightness-badge"
    class="flex justify-center text-sm text-slate-500"
  >
    <component
      :is="scrollTarget ? 'button' : 'span'"
      v-tooltip.bottom="tooltipText"
      :type="scrollTarget ? 'button' : undefined"
      :aria-label="scrollTarget ? ariaLabel : undefined"
      class="inline-flex items-center gap-2 rounded-md px-1"
      :class="scrollTarget
        ? 'cursor-pointer transition-colors hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400'
        : 'cursor-help'"
      @click="jumpToTarget"
    >
      <span>
        {{ $t('messages.quickValuation.tightness.title') }}
        <span
          v-if="hasPct"
          data-testid="qv-tightness-range"
          class="font-medium text-slate-700 tabular-nums"
        >{{ rangeLabel }}</span>
      </span>
      <Tag
        v-if="hasGrade"
        data-testid="qv-tightness-chip"
        :value="gradeLabel"
        :severity="severity"
        class="text-xs!"
      />
    </component>
  </div>
</template>

<script setup>
/**
 * RED-290 — per-valuation uncertainty badge (Jaan's method D), RED-363 sealed,
 * RED-356 words.
 *
 * Renders „Hindamistulemuse täpsus ±X %" plus a kõrge/keskpärane/madal chip.
 *
 *  - The chip is `grade` (Jaan Olev, 13.10): the server's TightnessPayload::grade
 *    on the valuation's own `u` — kitsas ≤ 0.08, keskmine ≤ 0.15, lai above;
 *    every direct result (Gate v0 backup included) always lai (RED-262, Jaan
 *    16.10) — never the sealed chip's `bin`.
 *    et.json's `tightness.bins` names them in the valuation standard's words
 *    (Toomas 02.10), and the lühiaruanne's `${hindamistulemuse-tapsus-tase}`
 *    prints the same grade from the same group, so screen and report agree.
 *  - The ±X % is the sealed `x_pct` (RED-363), shown only with the measurement's
 *    marker `tightness.sealed`: a row stored before RED-363 carries an
 *    in-sample `x_pct` (the "±7 %" on a price 40 % off), which never shows.
 *
 * The hover is the standard's definition alone: no share of sales "inside the
 * limits" (Jaan 13.10, no hit rate in words). No arrow; a click still jumps to
 * "Mudeli kvaliteet" when there is one.
 *
 * Cases: title + number + chip, title + chip, nothing (no grade and no sealed
 * number — no space is reserved).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { scrollToSectionId } from '@/composables/useScrollShortcuts'

const props = defineProps({
  tightness: { type: Object, default: null },
  /** `prediction.tightness_grade`: kitsas / keskmine / lai, or null. */
  grade: { type: String, default: null },
  /** DOM id to jump to on click (e.g. `model-quality`); null renders a plain span. */
  scrollTarget: { type: String, default: null },
})

const { t, te } = useI18n()

const GRADE_SEVERITY = {
  kitsas: 'success',
  keskmine: 'warn',
  lai: 'danger',
}

const isSealed = computed(() => typeof props.tightness?.sealed === 'string' && props.tightness.sealed !== '')
const hasPct = computed(() => isSealed.value && Number.isFinite(props.tightness?.x_pct))
const hasGrade = computed(() => props.grade !== null && te(`messages.quickValuation.tightness.bins.${props.grade}`))
const visible = computed(() => hasPct.value || hasGrade.value)

const rangeLabel = computed(() =>
  hasPct.value ? t('messages.quickValuation.tightness.range', { pct: props.tightness.x_pct }) : '',
)
const gradeLabel = computed(() =>
  hasGrade.value ? t(`messages.quickValuation.tightness.bins.${props.grade}`) : '',
)
const severity = computed(() => GRADE_SEVERITY[props.grade] ?? 'secondary')

const tooltipText = computed(() => t('messages.quickValuation.tightness.tooltip'))

const ariaLabel = computed(() =>
  hasPct.value
    ? t('messages.quickValuation.tightness.aria', { pct: props.tightness.x_pct, bin: gradeLabel.value })
    : t('messages.quickValuation.tightness.ariaChipOnly', { bin: gradeLabel.value }),
)

const jumpToTarget = () => {
  if (props.scrollTarget) {
    scrollToSectionId(props.scrollTarget, { offset: 90 })
  }
}
</script>
